import React, { ReactNode, useState, ChangeEvent, InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes, MouseEvent } from 'react';
import { AiOutlineLoading3Quarters, AiOutlineClose } from 'react-icons/ai';
import ErrorBoundary from './ErrorBoundary';

// ═══════════════════════════════════════════════════
// SPINNER
// ═══════════════════════════════════════════════════
export const Spinner = ({ size = 20 }: { size?: number }) => (
  <AiOutlineLoading3Quarters size={size} className="animate-spin" />
);
export const PageSpinner = () => (
  <div className="flex items-center justify-center py-24">
    <Spinner size={28} />
  </div>
);

// ═══════════════════════════════════════════════════
// BUTTON
// ═══════════════════════════════════════════════════
type BtnVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
const BV: Record<BtnVariant, string> = {
  primary:   'bg-p text-white hover:bg-p-deep border border-p-deep',
  secondary: 'bg-white text-ink-2 border border-ink-5 hover:bg-p-light hover:border-p-mid',
  danger:    'bg-red-600 text-white hover:bg-red-700 border border-red-700',
  ghost:     'text-ink-3 hover:bg-p-light border border-transparent',
};
interface BtnProps {
  variant?: BtnVariant; children: ReactNode; onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button'|'submit'|'reset'; disabled?: boolean; loading?: boolean; icon?: ReactNode;
  className?: string; size?: 'sm'|'md'; title?: string;
}
export const Button = ({ variant='primary', children, onClick, type='button', disabled, loading, icon, className='', size='md', title }: BtnProps) => {
  const pad = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  return (
    <button type={type} onClick={onClick} disabled={disabled||loading} title={title}
      className={`inline-flex items-center gap-2 rounded-sm font-medium transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${pad} ${BV[variant]} ${className}`}
      style={{ borderRadius: 'var(--r-sm)' }}>
      {loading ? <Spinner size={13} /> : icon}
      {children}
    </button>
  );
};

// ═══════════════════════════════════════════════════
// FIELD
// ═══════════════════════════════════════════════════
interface FieldProps { label?: string; error?: string; required?: boolean; children: ReactNode; className?: string }
export const Field = ({ label, error, required, children, className='' }: FieldProps) => (
  <div className={className}>
    {label && <label className="block text-xs font-semibold mb-1.5" style={{ color:'var(--ink-3)', letterSpacing:'.06em', textTransform:'uppercase' }}>
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>}
    {children}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const BASE = 'w-full px-3 py-2 rounded-sm text-sm transition-all outline-none border focus:ring-2 focus:ring-p/30 focus:border-p';
export const Input = ({ className='', ...props }: InputHTMLAttributes<HTMLInputElement> & { error?: boolean }) => (
  <input {...props} className={`${BASE} border-ink-5 bg-white text-ink placeholder-ink-4 ${className}`} style={{ borderRadius:'var(--r-xs)' }} />
);
export const Textarea = ({ className='', ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...props} className={`${BASE} border-ink-5 bg-white text-ink placeholder-ink-4 resize-none ${className}`} style={{ borderRadius:'var(--r-xs)' }} />
);
export const Select = ({ className='', children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...props} className={`${BASE} border-ink-5 bg-white text-ink ${className}`} style={{ borderRadius:'var(--r-xs)' }}>{children}</select>
);

// ═══════════════════════════════════════════════════
// BADGE
// ═══════════════════════════════════════════════════
type BadgeV = 'default'|'success'|'warning'|'danger'|'info';
const BDG: Record<BadgeV, string> = {
  default: 'bg-p-light text-p-deep', success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700', danger: 'bg-red-100 text-red-700', info: 'bg-blue-100 text-blue-700'
};
export const Badge = ({ children, variant='default' }: { children: ReactNode; variant?: BadgeV }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${BDG[variant]}`}>{children}</span>
);
export const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, BadgeV> = { draft:'default', sent:'info', approved:'success', rejected:'danger', expired:'warning', active:'success', disabled:'danger', admin:'info', sales:'default', partial:'warning', received:'success', cancelled:'danger' };
  return <Badge variant={map[status]??'default'}>{status}</Badge>;
};

// ═══════════════════════════════════════════════════
// MODAL
// ═══════════════════════════════════════════════════
const MSZ: Record<string, string> = { sm:'max-w-sm', md:'max-w-md', lg:'max-w-2xl', xl:'max-w-4xl' };
interface ModalProps { open:boolean; onClose:()=>void; title:string; children:ReactNode; size?:'sm'|'md'|'lg'|'xl'; footer?:ReactNode }
export const Modal = ({ open, onClose, title, children, size='md', footer }: ModalProps) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white w-full ${MSZ[size]} max-h-[90vh] flex flex-col animate-slide-up shadow-sh3`} style={{ borderRadius:'var(--r)' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-5">
          <h2 className="font-semibold text-ink text-sm">{title}</h2>
          <button onClick={onClose} className="text-ink-4 hover:text-ink-3 transition-colors p-1"><AiOutlineClose size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-ink-5 flex items-center justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
};

export const ConfirmModal = ({ open, onClose, onConfirm, title, message, loading, danger }:
  { open:boolean; onClose:()=>void; onConfirm:()=>void; title:string; message:string; loading?:boolean; danger?:boolean }) => (
  <Modal open={open} onClose={onClose} title={title} size="sm"
    footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant={danger?'danger':'primary'} onClick={onConfirm} loading={loading}>Confirm</Button></>}>
    <p className="text-sm text-ink-2">{message}</p>
  </Modal>
);

// ═══════════════════════════════════════════════════
// DATA TABLE
// ═══════════════════════════════════════════════════
export interface Column<T> { key:string; header:string; render:(row:T)=>ReactNode; width?:string }
interface TableProps<T> { columns:Column<T>[]; data:T[]; keyFn:(row:T)=>string; loading?:boolean; emptyText?:string }
export function DataTable<T>({ columns, data, keyFn, loading, emptyText='No records found.' }: TableProps<T>) {
  if (loading) return <PageSpinner />;
  return (
    <div className="overflow-x-auto border border-ink-5 shadow-sh1" style={{ borderRadius:'var(--r)' }}>
      <table className="w-full text-sm">
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key}
                style={{ background:'var(--p-pale)', color:'var(--ink-3)', width:c.width }}
                className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide border-b border-ink-5">
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length===0
            ? <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm" style={{ color:'var(--ink-4)' }}>{emptyText}</td></tr>
            : data.map(row => (
              <tr key={keyFn(row)} className="border-b border-ink-5 last:border-0 hover:bg-p-pale transition-colors">
                {columns.map(c => <td key={c.key} className="px-4 py-3" style={{ color:'var(--ink-2)' }}>{c.render(row)}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════
// PAGE HEADER
// ═══════════════════════════════════════════════════
export const PageHeader = ({ title, subtitle, action }: { title:string; subtitle?:string; action?:ReactNode }) => (
  <div className="flex items-center justify-between mb-6">
    <div>
      <h1 className="text-xl font-semibold" style={{ color:'var(--ink)' }}>{title}</h1>
      {subtitle && <p className="text-sm mt-0.5" style={{ color:'var(--ink-3)' }}>{subtitle}</p>}
    </div>
    {action && <div>{action}</div>}
  </div>
);

// ═══════════════════════════════════════════════════
// SEARCH BAR
// ═══════════════════════════════════════════════════
export const SearchBar = ({ value, onChange, placeholder='Search...' }: { value:string; onChange:(v:string)=>void; placeholder?:string }) => (
  <Input value={value} onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)} placeholder={placeholder} className="max-w-xs" />
);

// ═══════════════════════════════════════════════════
// CARD & STAT
// ═══════════════════════════════════════════════════
export const Card = ({ children, className='' }: { children:ReactNode; className?:string }) => (
  <div className={`bg-white border border-ink-5 shadow-sh1 ${className}`} style={{ borderRadius:'var(--r)' }}><ErrorBoundary>{children}</ErrorBoundary></div>
);
export const StatCard = ({ label, value, sub, accent }: { label:string; value:string|number; sub?:string; accent?:string }) => (
  <Card className="p-5">
    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color:'var(--ink-4)' }}>{label}</p>
    <p className={`text-2xl font-semibold ${accent||''}`} style={accent ? {} : { color:'var(--ink)' }}>{value}</p>
    {sub && <p className="text-xs mt-1" style={{ color:'var(--ink-4)' }}>{sub}</p>}
  </Card>
);

// ═══════════════════════════════════════════════════
// PAGINATION
// ═══════════════════════════════════════════════════
export const Pagination = ({ page, pages, total, onPage }: { page:number; pages:number; total:number; onPage:(p:number)=>void }) => {
  if (pages<=1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <span style={{ color:'var(--ink-4)' }}>{total} records</span>
      <div className="flex items-center gap-1">
        <Button variant="secondary" size="sm" onClick={() => onPage(page-1)} disabled={page<=1}>Prev</Button>
        <span className="px-3 text-xs" style={{ color:'var(--ink-3)' }}>Page {page} of {pages}</span>
        <Button variant="secondary" size="sm" onClick={() => onPage(page+1)} disabled={page>=pages}>Next</Button>
      </div>
    </div>
  );
};
